import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import type { TFunction } from 'i18next';
import { type ChangeEvent, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { SLOT_NAMES, slotNumberFromSlotName } from '../../../utils/constants';
import { getSaveSlotDisplayProgramName, isSaveSlotEmpty, type SaveSlotLike } from '../../../utils/saveSlotProjectId';

interface MoveSlotDialogProps {
  open: boolean;
  isMoving: boolean;
  confirmDisabled?: boolean;
  selectedDestinationKey: string | null;
  slotStorage: Record<string, SaveSlotLike | null | undefined>;
  sourceSlotKey: string | null;
  onCancel: () => void;
  onConfirm: () => void;
  onSelectDestination: (destinationKey: string) => void;
}

function getSlotLabel(slot: SaveSlotLike | null | undefined, slotNumber: string, isCurrentSlot: boolean, t: TFunction): string {
  const fallbackSlotName = t('moveSlotDialog.slotName', 'Slot {{slotNumber}}', { slotNumber });
  const slotName = isSaveSlotEmpty(slot)
    ? t('connectedDrawer.swerveSlot.emptySlot', 'Empty Slot')
    : getSaveSlotDisplayProgramName(slot?.programTitle) || fallbackSlotName;
  const slotLabel = t('moveSlotDialog.slotLabel', 'Slot {{slotNumber}}: {{slotName}}', { slotName, slotNumber });
  return isCurrentSlot ? t('moveSlotDialog.currentSlotLabel', '{{slotLabel}} (Current Slot)', { slotLabel }) : slotLabel;
}

const MoveSlotDialog = ({
  open,
  isMoving,
  confirmDisabled = false,
  selectedDestinationKey,
  slotStorage,
  sourceSlotKey,
  onCancel,
  onConfirm,
  onSelectDestination,
}: MoveSlotDialogProps) => {
  const { t, i18n } = useTranslation();
  const slotNumberFormatter = useMemo(() => new Intl.NumberFormat(i18n.resolvedLanguage || i18n.language), [i18n.language, i18n.resolvedLanguage]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onSelectDestination(event.target.value);
  };

  return (
    <Dialog open={open} onClose={isMoving ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle variant="dialogTitle" align="center">
        <Trans i18nKey="moveSlotDialog.title">Move to a Different Slot</Trans>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ py: 3, px: 3 }}>
        <Stack spacing={2}>
          <Typography variant="body1" component="p">
            <Trans i18nKey="moveSlotDialog.body">Choose the destination slot for this program on the Drive Base.</Trans>
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup value={selectedDestinationKey ?? ''} onChange={handleChange}>
              {SLOT_NAMES.map((slotKey) => {
                const slotNumber = slotNumberFromSlotName(slotKey);
                const formattedSlotNumber = slotNumberFormatter.format(slotNumber);
                const isCurrentSlot = sourceSlotKey === slotKey;
                const slot = slotStorage[slotKey];
                return (
                  <FormControlLabel
                    key={slotKey}
                    value={slotKey}
                    disabled={isCurrentSlot || isMoving}
                    control={<Radio />}
                    label={getSlotLabel(slot, formattedSlotNumber, isCurrentSlot, t)}
                  />
                );
              })}
            </RadioGroup>
          </FormControl>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'center', px: 2, py: 2 }}>
        <Stack direction="row" spacing={2}>
          <Button variant="text" onClick={onCancel} disabled={isMoving}>
            <Trans i18nKey="moveSlotDialog.cancel">Cancel</Trans>
          </Button>
          <Button variant="contained" onClick={onConfirm} disabled={isMoving || confirmDisabled || selectedDestinationKey === null}>
            <Trans i18nKey="moveSlotDialog.confirm">Move</Trans>
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default MoveSlotDialog;
